import { useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
  useManagedTimeout
} from 'design-system';
import { Mail } from 'lucide-react';
import { useAppSelector } from '@/store/hooks';

import { captureEvent } from '@/services/analytics';

import { METADATA_FIRST_NAME_FIELD, METADATA_LAST_NAME_FIELD } from '@/config/constants';
import { retrieveFromUserMetadata } from '@/services/storage/userMetadata';
const MAX_MESSAGE_LENGTH = 256;

const PremiumDialog = ({
  open,
  onClose,
  type
}: {
  open: boolean;
  onClose: () => void;
  type?: string;
}) => {
  const [value, setValue] = useState('');
  const [messageSent, setMessageSent] = useState(false);
  const { metadata, userEmail } = useAppSelector(state => state.tripToken);
  const { cancel: cancelReset, schedule: scheduleReset } = useManagedTimeout();

  useEffect(() => {
    if (open) {
      cancelReset();
      return;
    }

    scheduleReset(() => {
      setMessageSent(false);
      setValue('');
    }, 1000);
  }, [cancelReset, open, scheduleReset]);

  const premiumMessage = 'Premium';
  const enterpriseMessage = 'Enterprise';

  const firstName = retrieveFromUserMetadata(metadata || [], METADATA_FIRST_NAME_FIELD);
  const lastName = retrieveFromUserMetadata(metadata || [], METADATA_LAST_NAME_FIELD);

  const handleClose = () => {
    onClose();
  };

  const handleAction = () => {
    if (messageSent) {
      handleClose();
      return;
    }
    setMessageSent(true);
    captureEvent('premium_enquire_sent', {
      additionalInfo: value
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value;
    // Cut off if longer than MAX_MESSAGE_LENGTH (covers paste too)
    setValue(inputValue.slice(0, MAX_MESSAGE_LENGTH));
  };

  return (
    <Dialog
      onOpenChange={nextOpen => {
        if (!nextOpen) handleClose();
      }}
      open={open}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{messageSent ? 'Message sent!' : 'Get in touch'}</DialogTitle>
          <DialogDescription className='sr-only'>Contact GX Go about your plan.</DialogDescription>
        </DialogHeader>
        {messageSent ? (
          <div className='space-y-1'>
            <p>Thank you.</p>
            <p>We'll be in touch shortly!</p>
          </div>
        ) : (
          <div className='space-y-4'>
            <div className='space-y-1 font-bold'>
              <p>
                {firstName} {lastName}
              </p>
              {userEmail && <p>{userEmail}</p>}
            </div>
            <p className='rounded-md bg-muted p-4 text-sm'>
              Hi,
              <br />
              Can you tell me more about GX Go{' '}
              {type === 'enterprise' ? enterpriseMessage : premiumMessage}?
            </p>
            <div>
              <Textarea
                aria-label='Additional information'
                placeholder='(Optional) Enter any additional information'
                rows={2}
                value={value}
                onChange={handleChange}
              />
              <p className='mt-2 select-none text-right text-xs text-muted-foreground'>
                {value.length} / {MAX_MESSAGE_LENGTH}
              </p>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={handleAction}>
            {messageSent ? (
              'Close'
            ) : (
              <>
                <Mail data-icon='inline-start' />
                Send
              </>
            )}
          </Button>
          {!messageSent && (
            <Button
              variant='outline'
              onClick={handleClose}
            >
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PremiumDialog;
